import { FocusMonitor } from '@angular/cdk/a11y';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { UniqueSelectionDispatcher } from '@angular/cdk/collections';
import {
    AfterContentInit,
    ChangeDetectorRef,
    Directive,
    ElementRef,
    EventEmitter,
    forwardRef,
    Input,
    Optional,
    Output,
    Renderer2,
    ViewChild
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

// Increasing integer for generating unique ids for radio components.
let nextUniqueId = 0;

/**
 * Provider Expression that allows mat-radio-group to register as a ControlValueAccessor. This
 * allows it to support [(ngModel)] and ngControl.
 * @docs-protected
 */
export const RADIO_GROUP_CONTROL_VALUE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => RadioGroupDirective),
    multi: true
};

/** Change event object emitted by MatRadio and MatRadioGroup. */
export class RadioChange {
    constructor(
        /** The RadioComponentDef that emits the change event. */
        public source: RadioComponentDef,
        /** The value of the RadioComponentDef. */
        public value: any
    ) {}
}

/**
 * A group of radio buttons. May contain one or more `<radio>` elements.
 */
@Directive({
    standalone: true,
    selector: 'radio-group',
    providers: [RADIO_GROUP_CONTROL_VALUE_ACCESSOR],
    host: {
        role: 'radiogroup',
        class: 'radio-group'
    }
})
export class RadioGroupDirective implements AfterContentInit, ControlValueAccessor {
    // tslint:disable-next-line: variable-name
    static ngAcceptInputType_disabled: BooleanInput;
    // tslint:disable-next-line: variable-name
    static ngAcceptInputType_required: BooleanInput;

    /** Selected value for the radio group. */
    protected _value: any = null;

    /** The HTML name attribute applied to radio buttons in this group. */
    protected _name = `radio-group-${nextUniqueId++}`;

    /** The currently selected radio button. Should match value. */
    protected _selected: RadioComponentDef | null = null;

    /** Whether the `value` has been set to its initial value. */
    protected _isInitialized = false;

    /** Whether the radio group is disabled. */
    protected _disabled = false;

    /** Whether the radio group is required. */
    protected _required = false;

    /** Name of the radio button group. All radio buttons inside this group will use this name. */
    @Input()
    get name(): string {
        return this._name;
    }
    set name(value: string) {
        this._name = value;
        this.updateRadioNames();
    }

    /**
     * Value for the radio-group. Should equal the value of the selected radio button if there is
     * a corresponding radio button with a matching value. If there is not such a corresponding
     * radio button, this value persists to be applied in case a new radio button is added with a
     * matching value.
     */
    @Input()
    get value(): any {
        return this._value;
    }
    set value(newValue: any) {
        if (this._value !== newValue) {
            // Set this before proceeding to ensure no circular loop occurs with selection.
            this._value = newValue;

            this.updateSelectedRadioFromValue();
            this.checkSelectedRadio();
        }
    }

    /**
     * The currently selected radio button. If set to a new radio button, the radio group value
     * will be updated to match the new selected button.
     */
    @Input()
    get selected() {
        return this._selected;
    }
    set selected(selected: RadioComponentDef | null) {
        this._selected = selected;
        this.value = selected ? selected.value : null;
        this.checkSelectedRadio();
    }

    /** Whether the radio group is disabled */
    @Input()
    get disabled(): boolean {
        return this._disabled;
    }
    set disabled(value) {
        this._disabled = coerceBooleanProperty(value);
        this.detectChanges();
    }

    /** Whether the radio group is required */
    @Input()
    get required(): boolean {
        return this._required;
    }
    set required(value: boolean) {
        this._required = coerceBooleanProperty(value);
        this.detectChanges();
    }

    /**
     * Event emitted when the group value changes.
     * Change events are only emitted when the value changes due to user interaction with
     * a radio button (the same behavior as `<input type-"radio">`).
     */
    @Output()
    readonly change: EventEmitter<RadioChange> = new EventEmitter<RadioChange>();

    /** Child radio buttons. */
    private _radios: RadioComponentDef[] = [];

    constructor(protected changeDetectorRef: ChangeDetectorRef) {}

    /** The method to be called in order to update ngModel */
    onChange: (value: any) => void = () => {};

    /**
     * onTouch function registered via registerOnTouch (ControlValueAccessor).
     */
    onTouched: () => any = () => {};

    protected checkSelectedRadio() {
        if (this._selected && !this._selected.checked) {
            this._selected.checked = true;
            this.detectChanges();
        }
    }
    /**
     * Initialize properties once content children are available.
     * This allows us to propagate relevant attributes to associated buttons.
     */
    ngAfterContentInit() {
        // Mark this component as initialized in AfterContentInit because the initial value can
        // possibly be set by NgModel on MatRadioGroup, and it is possible that the OnInit of the
        // NgModel occurs *after* the OnInit of the MatRadioGroup.
        this._isInitialized = true;
        this.changeDetectorRef.detectChanges();
    }

    /**
     * Mark this group as being "touched" (for ngModel). Meant to be called by the contained
     * radio buttons upon their blur.
     */
    touch() {
        if (this.onTouched) {
            this.onTouched();
        }
    }

    protected updateRadioNames(): void {
        this._radios?.forEach(radio => {
            radio.name = this.name;
        });
        this.detectChanges();
    }

    protected detectChanges() {
        this._radios?.forEach(radio => {
            radio.detectChanges();
        });
    }

    /** Updates the `selected` radio button from the internal _value state. */
    protected updateSelectedRadioFromValue(): void {
        // If the value already matches the selected radio, do nothing.
        let _selected = null;
        this._radios?.forEach(radio => {
            radio.checked = this.value === radio.value;
            if (radio.checked) {
                _selected = radio;
            }
        });
        this._selected = _selected;
    }

    /** Dispatch change event with current selection and group value. */
    emitChangeEvent(): void {
        if (this._isInitialized) {
            this.change.emit(new RadioChange(this._selected, this._value));
        }
    }

    /**
     * Sets the model value. Implemented as part of ControlValueAccessor.
     * @param value
     */
    writeValue(value: any): void {
        this.value = value;
        if (this._isInitialized) {
            this.changeDetectorRef.detectChanges();
        }
    }

    /**
     * Registers a callback to be triggered when the model value changes.
     * Implemented as part of ControlValueAccessor.
     * @param fn Callback to be registered.
     */
    registerOnChange(fn: (value: any) => void): void {
        this.onChange = fn;
    }

    /**
     * Registers a callback to be triggered when the control is touched.
     * Implemented as part of ControlValueAccessor.
     * @param fn Callback to be registered.
     */
    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    /**
     * Sets the disabled state of the control. Implemented as a part of ControlValueAccessor.
     * @param isDisabled Whether the control should be disabled.
     */
    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
        if (this._isInitialized) {
            this.changeDetectorRef.detectChanges();
        }
    }

    register(radio: RadioComponentDef) {
        this.unregister(radio);
        this._radios.push(radio);
    }
    unregister(radio: RadioComponentDef) {
        const i = this._radios.indexOf(radio);
        if (i > -1) {
            this._radios.splice(i, 1);
        }
    }
}

@Directive()
// eslint-disable-next-line @angular-eslint/directive-class-suffix
export abstract class RadioComponentDef {
    protected readonly _uniqueId: string = `radio-${++nextUniqueId}`;

    /** The parent radio group. May or may not be present. */
    radioGroup: RadioGroupDirective;

    /** Whether this radio is checked. */
    private _checked = false;

    /** Whether this radio is disabled. */
    private _disabled: boolean;

    /** Whether this radio is required. */
    private _required: boolean;

    /** Value assigned to this radio. */
    private _value: any = null;

    /**
     * If component is initiated
     */
    private initiated = false;

    /** The unique ID for the radio button. */
    @Input()
    id: string = this._uniqueId;

    /** Analog to HTML 'name' attribute used to group radios for unique selection. */
    @Input()
    name: string;

    /** Used to set the 'aria-label' attribute on the underlying input element. */
    @Input('aria-label')
    ariaLabel: string;

    /** The 'aria-labelledby' attribute takes precedence as the element's text alternative. */
    @Input('aria-labelledby')
    ariaLabelledby: string;

    /** The 'aria-describedby' attribute is read after the element's label and field type. */
    @Input('aria-describedby')
    ariaDescribedby: string;

    /** Whether this radio button is checked. */
    @Input()
    get checked(): boolean {
        return this._checked;
    }
    set checked(value: boolean) {
        const newCheckedState = coerceBooleanProperty(value);
        if (this._checked !== newCheckedState) {
            this._checked = newCheckedState;
            if (newCheckedState && this.radioGroup && this.radioGroup.value !== this.value) {
                this.radioGroup.selected = this;
            } else if (!newCheckedState && this.radioGroup && this.radioGroup.value === this.value) {
                // When unchecking the selected radio button, update the selected radio
                // property on the group.
                this.radioGroup.selected = null;
            }

            if (newCheckedState) {
                // Notify all radio buttons with the same name to un-check.
                this.radioDispatcher.notify(this.id, this.name);
            }
            this.checked
                ? this.renderer2.addClass(this.elementRef.nativeElement, 'is-checked')
                : this.renderer2.removeClass(this.elementRef.nativeElement, 'is-checked');
            this.detectChanges();
        }
    }

    /** The value of this radio button. */
    @Input()
    get value(): any {
        return this._value;
    }
    set value(value: any) {
        if (this._value !== value) {
            this._value = value;
            if (this.radioGroup !== null) {
                if (!this.checked) {
                    // Update checked when the value changed to match the radio group's value
                    this.checked = this.radioGroup.value === value;
                }
                if (this.checked) {
                    this.radioGroup.selected = this;
                }
            }
        }
    }

    /** Whether the radio button is disabled. */
    @Input()
    get disabled(): boolean {
        return this._disabled || (this.radioGroup !== null && this.radioGroup.disabled);
    }
    set disabled(value: boolean) {
        this.setDisabled(coerceBooleanProperty(value));
    }

    /** Whether the radio button is required. */
    @Input()
    get required(): boolean {
        return this._required || (this.radioGroup && this.radioGroup.required);
    }
    set required(value: boolean) {
        this._required = coerceBooleanProperty(value);
    }

    /** ID of the native input element inside `<mat-radio-button>` */
    get inputId(): string {
        return `${this.id || this._uniqueId}-input`;
    }

    /**
     * Event emitted when the checked state of this radio button changes.
     * Change events are only emitted when the value changes due to user interaction with
     * the radio button (the same behavior as `<input type-"radio">`).
     */
    @Output()
    readonly change: EventEmitter<RadioChange> = new EventEmitter<RadioChange>();

    /** The native `<input type=radio>` element */
    @ViewChild('input')
    inputElement: ElementRef<HTMLInputElement>;

    constructor(
        @Optional() radioGroup: RadioGroupDirective,
        protected elementRef: ElementRef,
        protected focusMonitor: FocusMonitor,
        protected changeDetectorRef: ChangeDetectorRef,
        protected radioDispatcher: UniqueSelectionDispatcher,
        private renderer2: Renderer2
    ) {
        // Assertions. Ideally these should be stripped out by the compiler.
        // TODO(jelbourn): Assert that there's no name binding AND a parent radio group.
        this.radioGroup = radioGroup;
        if (radioGroup) {
            radioGroup.register(this);
        }

        this.removeUniqueSelectionListener = radioDispatcher.listen((id: string, name: string) => {
            if (id !== this.id && name === this.name) {
                this.checked = false;
            }
        });
    }

    ngOnInit() {
        if (this.radioGroup) {
            // If the radio is inside a radio group, determine if it should be checked
            this.checked = this.radioGroup.value === this._value;
            // Copy name from parent radio group
            this.name = this.radioGroup.name;
        }
        this.initiated = true;
        this.detectChanges();
    }

    ngAfterViewInit() {
        this.focusMonitor.monitor(this.elementRef, true).subscribe(focusOrigin => {
            if (!focusOrigin && this.radioGroup) {
                this.radioGroup.touch();
            }
        });
    }

    ngOnDestroy() {
        this.focusMonitor.stopMonitoring(this.elementRef);
        this.removeUniqueSelectionListener();
        this.radioGroup?.unregister(this);
    }

    /** Unregister function for _radioDispatcher */
    protected removeUniqueSelectionListener: () => void = () => {};

    /**
     * Marks the radio button as needing checking for change detection.
     * This method is exposed because the parent radio group will directly
     * update bound properties of the radio button.
     */
    detectChanges() {
        // When group value changes, the button will not be notified. Use `detectChanges` to explicit
        // update radio button's status
        if (this.initiated) {
            this.changeDetectorRef.detectChanges();
        }
    }

    /** Focuses the radio button. */
    focus(options?: FocusOptions): void {
        this.focusMonitor.focusVia(this.inputElement, 'keyboard', options);
    }

    /** Dispatch change event with current value. */
    protected emitChangeEvent(): void {
        this.change.emit(new RadioChange(this, this._value));
    }

    onInputClick(event: Event) {
        // We have to stop propagation for click events on the visual hidden input element.
        // By default, when a user clicks on a label element, a generated click event will be
        // dispatched on the associated input element. Since we are using a label element as our
        // root container, the click event on the `radio-button` will be executed twice.
        // The real click event will bubble up, and the generated click event also tries to bubble up.
        // This will lead to multiple click events.
        // Preventing bubbling for the second event will solve that issue.
        event.stopPropagation();
    }

    /**
     * Triggered when the radio button received a click or the input recognized any change.
     * Clicking on a label element, will trigger a change event on the associated input.
     */
    onInputChange(event: Event) {
        // We always have to stop propagation on the change event.
        // Otherwise the change event, from the input element, will bubble up and
        // emit its event object to the `change` output.
        event.stopPropagation();

        const groupValueChanged = this.radioGroup && this.value !== this.radioGroup.value;
        this.checked = true;
        this.emitChangeEvent();
        if (this.radioGroup) {
            this.radioGroup.onChange(this.value);

            if (groupValueChanged) {
                this.radioGroup.emitChangeEvent();
            }
        }
    }

    /** Sets the disabled state and marks for check if a change occurred. */
    protected setDisabled(value: boolean) {
        if (this._disabled !== value) {
            this._disabled = value;
            this.disabled
                ? this.renderer2.addClass(this.elementRef.nativeElement, 'is-disabled')
                : this.renderer2.removeClass(this.elementRef.nativeElement, 'is-disabled');
            this.changeDetectorRef.detectChanges();
        }
    }
}
